/**
 * BusinessTermCard Component
 *
 * Reusable card component for displaying and editing business terms
 * with click-anywhere-to-edit functionality and full 508 accessibility compliance.
 */

import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import businessTermService, { BusinessTerm, BusinessTermUpdateInput } from '../../services/businessTermService';

interface BusinessTermCardProps {
  term: BusinessTerm;
  onEdit: (id: string, data: BusinessTermUpdateInput) => Promise<void>;
  isEditing?: boolean;
  onEditStart?: (id: string) => void;
  onEditCancel?: (id: string) => void;
  compact?: boolean;
  showEditButton?: boolean;
}

const BusinessTermCard: React.FC<BusinessTermCardProps> = ({
  term,
  onEdit,
  isEditing = false,
  onEditStart,
  onEditCancel,
  compact = false,
  showEditButton = false
}) => {
  const [editedTerm, setEditedTerm] = useState<BusinessTermUpdateInput>({
    name: term.name,
    definition: term.definition,
    category: term.category,
    privacyClassification: term.privacyClassification,
    sensitivityLevel: term.sensitivityLevel,
    dataElementLabel: term.dataElementLabel,
    dataPrivacyImpact: term.dataPrivacyImpact
  });
  const [saving, setSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      onEditStart?.(term._id);
      // Focus the first input field after state updates
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditCancel?.(term._id);
    // Reset edited term to original values
    setEditedTerm({
      name: term.name,
      definition: term.definition,
      category: term.category,
      privacyClassification: term.privacyClassification,
      sensitivityLevel: term.sensitivityLevel,
      dataElementLabel: term.dataElementLabel,
      dataPrivacyImpact: term.dataPrivacyImpact
    });
  };

  const handleSaveChanges = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (saving) return;

    setSaving(true);
    try {
      await onEdit(term._id, editedTerm);
      onEditCancel?.(term._id);
    } catch (error) {
      console.error('Failed to save term:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof BusinessTermUpdateInput, value: any) => {
    setEditedTerm((prev: BusinessTermUpdateInput) => ({ ...prev, [field]: value }));
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit(e as any);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return '#4CAF50'; // Green
      case 'pending-review':
        return '#FF9800'; // Orange
      case 'draft':
        return '#2196F3'; // Blue
      case 'deprecated':
        return '#9E9E9E'; // Gray
      case 'archived':
        return '#9E9E9E'; // Gray
      default:
        return '#9E9E9E'; // Gray
    }
  };

  const displayTerm = isEditing ? editedTerm : term;

  return (
    <Card
      sx={{
        height: '100%',
        cursor: isEditing ? 'default' : 'pointer',
        '&:hover': isEditing ? {} : { boxShadow: 3 },
        '&:focus-within': { boxShadow: 3, outline: '2px solid #1785FB' },
        position: 'relative',
        transition: 'box-shadow 0.3s, outline 0.3s',
        backgroundColor: isEditing ? '#f5f5f5' : 'white'
      }}
      tabIndex={isEditing ? -1 : 0}
      role="button"
      aria-label={isEditing ? `Editing ${term.name}` : `Click to edit ${term.name}`}
      onKeyDown={(e) => {
        if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleEditClick(e as any);
        }
      }}
      onClick={(e) => {
        if (!isEditing) {
          handleEditClick(e);
        }
      }}
    >
      <CardContent onKeyDown={isEditing ? handleEditKeyDown : undefined}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1
          }}
        >
          {isEditing ? (
            <TextField
              inputRef={nameInputRef}
              fullWidth
              label="Term Name"
              value={displayTerm.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{ mr: 1 }}
              inputProps={{
                'aria-label': 'Edit term name'
              }}
            />
          ) : (
            <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
              {displayTerm.name}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showEditButton && !isEditing && (
              <IconButton
                size="small"
                onClick={handleEditClick}
                aria-label="Edit term"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <Chip
              label={term.status}
              size="small"
              sx={{
                backgroundColor: getStatusColor(term.status),
                color: 'white',
                fontWeight: 500
              }}
            />
          </Box>
        </Box>

        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={compact ? 3 : 4}
            label="Definition"
            value={displayTerm.definition || ''}
            onChange={(e) => handleFieldChange('definition', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            sx={{ mt: 1, mb: 2 }}
            inputProps={{
              'aria-label': 'Edit definition'
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              mb: 2,
              color: 'text.secondary'
            }}
          >
            {displayTerm.definition}
          </Typography>
        )}

        {/* Unidatum-Standard Fields (Edit Mode Only) */}
        {isEditing && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
              Privacy & Classification
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Privacy Classification</InputLabel>
                  <Select
                    value={editedTerm.privacyClassification || 'Internal'}
                    label="Privacy Classification"
                    onChange={(e) => handleFieldChange('privacyClassification', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MenuItem value="Public">Public</MenuItem>
                    <MenuItem value="Internal">Internal</MenuItem>
                    <MenuItem value="Confidential">Confidential</MenuItem>
                    <MenuItem value="Highly Confidential">Highly Confidential</MenuItem>
                    <MenuItem value="PII">PII (Personally Identifiable)</MenuItem>
                    <MenuItem value="PHI">PHI (Protected Health Info)</MenuItem>
                    <MenuItem value="PCI">PCI (Payment Card Info)</MenuItem>
                    <MenuItem value="Restricted">Restricted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sensitivity Level</InputLabel>
                  <Select
                    value={editedTerm.sensitivityLevel || 'Medium'}
                    label="Sensitivity Level"
                    onChange={(e) => handleFieldChange('sensitivityLevel', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data Element Label"
                  value={editedTerm.dataElementLabel || ''}
                  onChange={(e) => handleFieldChange('dataElementLabel', e.target.value.toUpperCase())}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="e.g., CUST_FIRST_NAME"
                  helperText="Standardized uppercase label"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Retention Period"
                  value={editedTerm.dataPrivacyImpact?.retentionPeriod || ''}
                  onChange={(e) => handleFieldChange('dataPrivacyImpact', {
                    ...editedTerm.dataPrivacyImpact,
                    retentionPeriod: e.target.value
                  })}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="e.g., 7 years, indefinite"
                />
              </Grid>
            </Grid>

            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              Compliance Flags:
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                { key: 'gdprRelevant', label: 'GDPR' },
                { key: 'ccpaRelevant', label: 'CCPA' },
                { key: 'hipaaRelevant', label: 'HIPAA' },
                { key: 'pciRelevant', label: 'PCI' }
              ].map(({ key, label }) => (
                <Grid item xs={6} sm={3} key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={(editedTerm.dataPrivacyImpact as any)?.[key] || false}
                        onChange={(e) => handleFieldChange('dataPrivacyImpact', {
                          ...editedTerm.dataPrivacyImpact,
                          [key]: e.target.checked
                        })}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={label}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {!isEditing && (
          <>
            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
              <strong>Domain:</strong> {term.domain?.name || 'Uncategorized'}
            </Typography>

            <Typography variant="caption" display="block" sx={{ mb: 1 }}>
              <strong>Steward:</strong> {term.owner?.name || term.stewards?.[0]?.name || 'Unassigned'}
            </Typography>

            <Typography variant="caption" display="block" sx={{ mb: 2 }}>
              <strong>Last Updated:</strong> {term.updatedAt ? new Date(term.updatedAt).toLocaleDateString() : 'N/A'}
            </Typography>

            {/* Examples */}
            {term.examples && term.examples.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  Examples:
                </Typography>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                  {term.examples.map((example: string, index: number) => (
                    <li key={index}>
                      <Typography variant="caption">{example}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}

            {/* Related Terms */}
            {term.relatedTerms && term.relatedTerms.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                  Related Terms:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {term.relatedTerms.map((relatedTerm: any) => (
                    <Chip
                      key={relatedTerm._id}
                      label={relatedTerm.termId.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Could implement navigation to related term
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Tags */}
            {term.tags && term.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {term.tags.map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </>
        )}

        {/* Edit/Save/Cancel Buttons */}
        {isEditing && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              size="small"
              disabled={saving}
              aria-label="Save changes"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={handleCancelEdit}
              size="small"
              disabled={saving}
              aria-label="Cancel editing"
            >
              Cancel
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BusinessTermCard;
